import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  /** Changing this value resets the boundary (we pass the current route path). */
  resetKey: string;
  onReset: () => void;
  /** Translator passed from the functional wrapper (class can't use hooks). */
  t: TFunction;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches render-time errors thrown by a routed page so a single broken page
 * shows a recoverable fallback instead of unmounting the entire app (which is
 * what blanks the screen and breaks back-navigation when there is no boundary).
 *
 * The boundary automatically clears its error state when the route changes, so
 * navigating away from the broken page recovers without a full reload.
 */
class ErrorBoundaryInner extends React.Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface the error for debugging without taking the whole app down.
    console.error('Route render error:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps) {
    // Reset when the user navigates to a different route.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      const { t } = this.props;
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" p={3}>
          <Stack spacing={2} alignItems="center" maxWidth={480} textAlign="center">
            <Typography variant="h6" fontWeight={700}>
              {t('errorHandling.boundary.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('errorHandling.boundary.message')}
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  this.props.onReset();
                }}
              >
                {t('errorHandling.boundary.retry')}
              </Button>
              <Button variant="outlined" onClick={() => window.location.assign('/dashboard')}>
                {t('errorHandling.boundary.goDashboard')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}

const RouteErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <ErrorBoundaryInner resetKey={location.pathname} onReset={() => navigate(0)} t={t}>
      {children}
    </ErrorBoundaryInner>
  );
};

export default RouteErrorBoundary;
